/**
 * NotificationsBell — partner shell top-right widget.
 *
 * Polls /api/partner/notifications every 60s, shows an unread-count badge
 * on a bell icon, and opens a dropdown listing recent alerts. Currently
 * carries channel_auto_disabled events but is intentionally generic so
 * future notification types (dispute, refund, KYC, etc.) plug in cleanly.
 */
import { useEffect, useRef, useState } from "react";
import { Bell, X, ShieldOff, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

export default function NotificationsBell() {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const nav = useNavigate();

    const load = async () => {
        try {
            const r = await api.get("/partner/notifications");
            setItems(r.data.items || []);
            setUnread(r.data.unread_count || 0);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 60_000);
        return () => clearInterval(t);
    }, []);

    // Close on outside click
    useEffect(() => {
        const onClick = (e) => {
            if (open && containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const ack = async (id) => {
        try {
            await api.post(`/partner/notifications/${id}/ack`);
            load();
        } catch { /* ignore */ }
    };

    return (
        <div ref={containerRef} className="relative" data-testid="notif-bell-container">
            <button type="button" onClick={() => setOpen(!open)}
                    data-testid="notif-bell-btn"
                    className="relative p-2 rounded-md hover:bg-slate-100 transition-colors" aria-label="Notifications">
                <Bell className="w-5 h-5 text-slate-700" />
                {unread > 0 && (
                    <span data-testid="notif-unread-count"
                          className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center">
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-[min(24rem,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl z-50"
                     data-testid="notif-dropdown">
                    <div className="flex items-center justify-between p-3 border-b border-slate-200 sticky top-0 bg-white">
                        <div className="font-semibold text-sm">Notifications</div>
                        <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-800">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    {items.length === 0 ? (
                        <div className="p-6 text-center text-xs text-slate-400">No notifications yet.</div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {items.map((n) => (
                                <div key={n.id} data-testid={`notif-item-${n.id}`}
                                     className={`p-3 flex items-start gap-3 ${n.ack ? "opacity-60" : "bg-amber-50/40"}`}>
                                    {n.type === "payin_stuck" ? <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> : <ShieldOff className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                        {n.type === "payin_stuck" ? (
                                            <>
                                                <div className="text-xs font-medium text-slate-800">PayIn orders stuck in progress</div>
                                                <div className="text-[11px] text-slate-600 mt-0.5">{n.count} order{n.count === 1 ? "" : "s"} still in progress after {n.threshold_minutes} min — follow up with the customer.</div>
                                                <button type="button" data-testid={`notif-review-${n.id}`}
                                                        onClick={() => { setOpen(false); if (!n.ack) ack(n.id); nav("/partner/panel/payin-history?stuck=1"); }}
                                                        className="mt-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800">Review →</button>
                                            </>
                                        ) : (
                                            <>
                                                <div className="text-xs font-medium text-slate-800">
                                                    {n.type === "channel_auto_disabled" ? "Channel auto-disabled" : n.type}
                                                </div>
                                                <div className="text-[11px] text-slate-600 mt-0.5">
                                                    <span className="font-medium">{n.channel_label}</span> ({n.channel_type})
                                                    {" — "}{n.reason}
                                                </div>
                                            </>
                                        )}
                                        <div className="text-[10px] text-slate-400 mt-1">
                                            {new Date(n.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                    {!n.ack && (
                                        <button data-testid={`notif-ack-${n.id}`} type="button" onClick={() => ack(n.id)}
                                                className="text-[10px] px-2 py-1 rounded bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 flex-shrink-0">
                                            Mark read
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
